/**
 * AlertStrip — the platform-health alerts banner at the top of the Overview cockpit. Renders ONLY the
 * alerts that fired (an empty list renders nothing, so the strip collapses). Each card is tone-tinted
 * (danger/warning/info → the clinical tint tokens), carries a glyph + count + one-line message (color is
 * NEVER the only signal — glyph + text always accompany it, daltonisme + AA), and a ghost CTA that deep-links
 * into the relevant section with a pre-applied filter.
 *
 * a11y: the strip is a role="region" labelled "Alertes plateforme"; each card is a role="status" so a
 * screen reader announces it. Token-driven (no raw hex, L-2). Mirrors under dir="rtl" via logical flex.
 */
import { useNavigate } from "react-router-dom";
import { Button } from "@/ui";
import { useT } from "@/app";
import type { AlertItem, AlertTone } from "../metrics.lib";

/** tone → { tint background, accent foreground } — all semantic tokens. */
const TONE_STYLE: Record<AlertTone, { tint: string; fg: string }> = {
  danger: { tint: "var(--nv-tint-critical)", fg: "var(--nv-danger, var(--nv-critical))" },
  warning: { tint: "var(--nv-tint-high)", fg: "var(--nv-warning, var(--nv-high))" },
  info: { tint: "var(--nv-tint-hold)", fg: "var(--nv-info, var(--nv-hold))" },
};

export function AlertStrip({ alerts }: { alerts: ReadonlyArray<AlertItem> }) {
  const { t } = useT();
  const navigate = useNavigate();

  if (alerts.length === 0) return null;

  return (
    <section
      role="region"
      aria-label={t("mgmt.overview.alerts.title")}
      style={{
        display: "grid",
        gap: "var(--nv-s-3)",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      }}
    >
      {alerts.map((a) => {
        const tone = TONE_STYLE[a.tone];
        const message = t(a.labelKey, { count: a.count, ...(a.vars ?? {}) });
        return (
          <div
            key={a.id}
            role="status"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--nv-s-3)",
              padding: "var(--nv-s-3)",
              borderRadius: "var(--nv-r-md)",
              background: tone.tint,
              border: "1px solid var(--nv-line-faint)",
            }}
          >
            <span
              aria-hidden
              style={{
                fontSize: "var(--nv-fs-h3)",
                color: tone.fg,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              {a.glyph}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="nv-ltr-island"
                style={{
                  fontFamily: "var(--nv-font-mono)",
                  fontWeight: 600,
                  fontSize: "var(--nv-fs-body-lg)",
                  color: tone.fg,
                  lineHeight: 1.1,
                }}
              >
                {a.count}
              </div>
              <div style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-1, var(--nv-graphite))" }}>
                {message}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate(a.to)}>
              {t(a.ctaKey)}
            </Button>
          </div>
        );
      })}
    </section>
  );
}
